import { useMutation, useQuery as useConvexQuery, useAction } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "@clerk/clerk-react";
import { toConvexId } from "@/types/convex";

interface CreateUserData {
  userType: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  familyGroupId?: string;
  password?: string; // Required for parent users
}

interface CreateUserResult {
  userId: string;
  deviceId?: string;
}

export const useCreateUser = () => {
  const { toast } = useToast();
  const { user: clerkUser } = useUser();
  const createUserMutation = useMutation(api.users.create.user);
  const createManagerAction = useAction(api.users.create.manager);
  const createGroupMutation = useMutation(api.groups.create.group);
  const createDeviceRegistrationMutation = useMutation(api.devices.registrations.create);

  const createUser = async (userData: CreateUserData): Promise<CreateUserResult> => {
      console.log('Creating user with data:', userData);
    try {
      // Step 1: Create user in Convex using appropriate endpoint
      let userId;
      let deviceId;
      
      if (userData.userType === "parent") {
        if (!userData.password) {
          throw new Error("Password is required for parent users");
        }
        userId = await createManagerAction({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          phone: userData.phone,
          password: userData.password,
          identity: "parent",
        });
      } else if (userData.userType === "self_managed") {
        if (!userData.password) {
          throw new Error("Password is required for self managed users");
        }
        console.log("Creating manager with payload:", {
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          phone: userData.phone,
          password: userData.password,
          identity: "self-managed",
        });
        userId = await createManagerAction({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          phone: userData.phone,
          password: userData.password,
          identity: "self-managed",
        });
        
        // Register device for self-managed user (no group required)
        console.log("Registering device for self-managed user:", userId);
        console.log("Registering device with payload:", {
          user: userId,
          email: userData.email,
          adBlock: false,
          restrictions: {}
        });
        
        deviceId = await createDeviceRegistrationMutation({
          user: userId,
          email: userData.email,
          adBlock: false,
          restrictions: {}
        });

        console.log("Device registered successfully:", deviceId);
      } else if (userData.userType === "mentor") {
        if (!userData.password) {
          throw new Error("Password is required for mentor users");
        }
        userId = await createManagerAction({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          phone: userData.phone,
          password: userData.password,
          identity: "mentor",
        });
      } else {
        // For child users, include identity field
        const userPayload: any = {
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          phone: userData.phone,
        };
        
        if (userData.userType === "child") {
          userPayload.identity = "child";
        }
        
        userId = await createUserMutation(userPayload);
      }

      // Step 3: Handle user type specific actions
      if (userData.userType === "parent") {
        await createGroupMutation({
          name: `${userData.firstName} ${userData.lastName}'s Family`,
          type: "family",
          user: userId,
        });

        toast({
          title: "Parent Created",
          description: `Parent user and family group created successfully for ${userData.firstName} ${userData.lastName}.`,
        });
      } else if (userData.userType === "child" && userData.familyGroupId) {
        // Step 4: Register device for child users
        console.log("Registering device for child user:", userId, "in group:", userData.familyGroupId);
        
        deviceId = await createDeviceRegistrationMutation({
          group: toConvexId.group(userData.familyGroupId),
          user: userId,
          adBlock: false,
          restrictions: {}
        });

        console.log("Device registered successfully:", deviceId);

        toast({
          title: "Child User Created",
          description: `Child user created and device registered successfully for ${userData.firstName} ${userData.lastName}.`,
        });
      } else if (userData.userType === "self_managed") {
        toast({
          title: "Self-Managed User Created",
          description: `User created and device registered successfully for ${userData.firstName} ${userData.lastName}.`,
        });
      } else {
        toast({
          title: "User Created",
          description: `${userData.userType} user created successfully for ${userData.firstName} ${userData.lastName}.`,
        });
      }

      return { userId, deviceId };
    } catch (error) {
      console.error("Failed to create user:", error);
      console.error("Error details:", {
        message: error?.message,
        cause: error?.cause,
        stack: error?.stack
      });
      toast({
        title: "Error",
        description: `Failed to create user: ${error?.message || 'Unknown error'}`,
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    createUser,
  };
};